/**
 * Error Boundary Component
 * Catches errors in child components and displays fallback UI
 */

import React, { ReactElement } from 'react'
import { WarningIcon } from '@bizfirst/common-react'

export interface ErrorBoundaryProps {
  children: React.ReactNode
  fallback?: ReactElement
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void
}

export interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo)
    this.props.onError?.(error, errorInfo)
  }

  render(): ReactElement {
    if (this.state.hasError) {
      return (
        <>
          {this.props.fallback || (
            <div
              style={{
                padding: '16px',
                backgroundColor: '#fee2e2',
                borderRadius: '8px',
                border: '1px solid #fecaca',
                color: '#991b1b',
                fontSize: '14px',
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <WarningIcon size={16} color="#991b1b" /> Error Loading Component
              </div>
              <details style={{ cursor: 'pointer' }}>
                <summary style={{ fontWeight: 500 }}>
                  {this.state.error?.message || 'Unknown error'}
                </summary>
                <pre
                  style={{
                    marginTop: '8px',
                    fontSize: '12px',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                  }}
                >
                  {this.state.error?.stack}
                </pre>
              </details>
            </div>
          )}
        </>
      )
    }

    return <>{this.props.children}</>
  }
}
